import { Rhythm, Text } from '@trmediaab/zebra';
import { Home } from '@trmediaab/zebra-icons';
import { formatPostalCode } from '@trmediaab/zebra-utils';

import Card from '../../components/Card';
import { Input, SelectInput } from '../../components/Form';
import TextWithAdjacentIcon from '../../components/TextWithAdjacentIcon';
import { useCountryOptions } from '../../utils/useCountryOptions';
import type { Action } from './Address';
import type { NewOrExistingUserWithShippingAddress } from './types';

interface AddressFormProps {
  values: NewOrExistingUserWithShippingAddress;
  disabled: boolean;
  isShippingAddress?: boolean;
  dispatch: React.Dispatch<Action>;
}

const AddressForm = ({
  values,
  disabled,
  isShippingAddress = false,
  dispatch,
}: AddressFormProps) => {
  const countries = useCountryOptions(isShippingAddress);

  return (
    <Card>
      <TextWithAdjacentIcon
        icon={<Home size="18px" color="checkout.icon1" />}
        text="Adressupgifter"
        fontWeight="bold"
      />
      <Text>Var vänlig fyll i dina adressupgifter</Text>
      <Rhythm between="4" before="4" after="2">
        <Input
          name="firstName"
          label="Förnamn"
          value={values.firstName}
          disabled={disabled}
          placeholder="Ange ditt förnamn"
          autoComplete="given-name"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            dispatch({ type: 'firstName', payload: e.target.value })
          }
        />
        <Input
          name="lastName"
          label="Efternamn"
          value={values.lastName}
          disabled={disabled}
          placeholder="Ange ditt efternamn"
          autoComplete="family-name"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            dispatch({ type: 'lastName', payload: e.target.value })
          }
        />
        <Input
          name="address"
          label="Adress"
          value={values.address.address}
          disabled={disabled}
          placeholder="Ange din adress"
          autoComplete="address-line1"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            dispatch({ type: 'address', payload: e.target.value })
          }
        />
        <Input
          name="careOf"
          label="c/o"
          value={values.address.careOf}
          disabled={disabled}
          placeholder="Ange eventuell c/o"
          autoComplete="address-line2"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            dispatch({ type: 'careOf', payload: e.target.value })
          }
        />
        <Input
          type="tel"
          name="postalCode"
          label="Postnummer"
          value={values.address.postalCode}
          disabled={disabled}
          placeholder="Ange ditt postnummer"
          autoComplete="postal-code"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            dispatch({ type: 'postalCode', payload: e.target.value })
          }
          onBlur={() => {
            dispatch({
              type: 'postalCode',
              payload: formatPostalCode(values.address.postalCode),
            });
          }}
        />
        <Input
          name="city"
          label="Postadress"
          value={values.address.city}
          disabled={disabled}
          placeholder="Ange din postadress"
          autoComplete="address-level2"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            dispatch({ type: 'city', payload: e.target.value })
          }
        />
        {isShippingAddress && (
          <>
            <Input
              type="tel"
              name="mobile"
              label="Mobilnummer"
              value={values.mobile}
              disabled={disabled}
              placeholder="Ange ditt mobiltelefonnummer"
              autoComplete="tel"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                dispatch({ type: 'mobile', payload: e.target.value })
              }
            />
            <SelectInput
              name="country"
              label="Land"
              description="För att få rätt portokostnad behöver du ange vilket land din produkt ska skickas till."
              value={values.address.country}
              data={[{ value: '', label: 'Välj land' }, ...countries]}
              disabled={disabled}
              width="100%"
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                dispatch({ type: 'country', payload: e.target.value })
              }
            />
          </>
        )}
      </Rhythm>
    </Card>
  );
};

export default AddressForm;
